import { createStyles, makeStyles, Theme } from '@material-ui/core';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';

import React, { useState, useEffect, useCallback } from 'react';
import { baseTheme } from '../../../theme';
import { groupNameExists } from '../../../services/groups/service';
import { useSelector } from 'react-redux';

export interface IGroupFormData {
  id?: string;
  name: string;
  type: string;
  members?: object[];
}

interface IGroupModalProps {
  open: boolean;
  editing?: boolean | undefined;
  groupType: string | undefined | null;
  propFormValues?: IGroupFormData | null;
  handleCallBack: (values: IGroupFormData | null) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
    },
    publicGroup: {
      backgroundColor: theme.palette.primary.main,
      borderRadius: '5px',
    },
    privateGroup: {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '5px',
    },
    whiteText: {
      color: theme.palette.background.default,
    },
    publicSeletion: {
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: `${theme.palette.background.default} !important`,
    },
    privateSelection: {
      backgroundColor: `${theme.palette.secondary.main} !important`,
      color: `${theme.palette.background.default} !important`,
    },
    selectMenuOverrides: {
      color: theme.palette.background.default,
    },
  }),
);

const defaultFormValues: IGroupFormData = {
  id: '',
  name: '',
  type: 'Closed',
  members: [],
};

export function GroupModal(props: IGroupModalProps) {
  const {
    open = false,
    handleCallBack,
    propFormValues = defaultFormValues,
    editing = false,
  } = props;
  const classes = useStyles(baseTheme);
  const [nameError, setNameError] = useState({ value: false, message: '' });
  const currUserID = useSelector((state: any) => state.auth.currUser.uid);
  const [fullWidth, setFullWidth] = React.useState(true);
  const [maxWidth, setMaxWidth] = React.useState<DialogProps['maxWidth']>('sm');

  const getFormValues = useCallback(
    () =>
      propFormValues && propFormValues.type
        ? propFormValues
        : defaultFormValues,
    [propFormValues],
  );
  const [formValues, setFormValues] = useState(defaultFormValues);

  useEffect(() => {
    setFormValues(getFormValues());
  }, [getFormValues]);

  const nameLengthValidation = (name: string) => {
    if (name.length) {
      setNameError({ value: false, message: '' });
      return true;
    }

    setNameError({ value: true, message: 'Please enter a Group Name' });
    return false;
  };

  const nameValidation = async (name: string) => {
    if (!nameLengthValidation(name)) {
      return false;
    }
    if (await groupNameExists(currUserID, name)) {
      setNameError({ value: true, message: 'You already have a group with that name. Please choose another name.' });
      return false;
    }

    setNameError({ value: false, message: '' });
    return true;
  };

  const handleFormChange = (prop: any) => (event: any) => {
    setFormValues({ ...formValues, [prop]: event.target.value });
    if (prop === 'name') {
      nameLengthValidation(event.target.value);
    }
  };

  const handleCancel = () => {
    setNameError({ value: false, message: '' });
    setFormValues(getFormValues);
    handleCallBack(null);
  };

  const handleDone = async () => {
    if (await nameValidation(formValues.name)) {
      setFormValues(getFormValues);
      handleCallBack(formValues);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
    >
      <DialogTitle id="group-management-form">{`${editing ? 'Update' : 'Add'} Group${editing ? ' Name' : ''}`}</DialogTitle>
      <DialogContent>
        <DialogContentText>

        </DialogContentText>
        <TextField
          error={nameError.value}
          helperText={nameError.message}
          variant="outlined"
          autoFocus
          margin="dense"
          id="name"
          label="Group Name"
          type="text"
          fullWidth
          value={formValues.name}
          onChange={handleFormChange('name')}
        />

        {!editing &&
        <FormControl variant="outlined" fullWidth size={'small'}
                      className={`${classes.whiteText} ${formValues.type === 'Closed' ? classes.privateGroup : classes.publicGroup}`}>
          <Select
              autoWidth
              labelId="group-type-select-label"
              id="group-type-select"
              value={formValues.type}
              onChange={handleFormChange('type')}
              color="primary"
              classes={{
                root: classes.whiteText,
                icon: classes.whiteText,
                selectMenu: classes.selectMenuOverrides,
              }}
          >
            <MenuItem
                value={'Public'}
                // className={classes.publicSeletion}
                classes={{
                  root: classes.publicSeletion,
                  selected: classes.publicSeletion,
                }}
            >
              Public Group
            </MenuItem>
            <MenuItem
                value={'Closed'}
                // className={classes.privateSelection}
                classes={{
                  root: classes.privateSelection,
                  selected: classes.privateSelection,
                }}
            >
              Closed Group
            </MenuItem>
          </Select>
        </FormControl>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>
          Cancel
        </Button>

        <Button onClick={handleDone}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
